import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * Cash drawers: what went into a till, what came out, and what was left.
 *
 * A *check-in* is one person's session on one lane — opened with a starting
 * float, closed with a count. A *movement* is a single cash event inside it:
 * a sale paid in cash, a drop into the safe, a correction. The report answers
 * both together, plus the totals across the whole search.
 *
 * The figure that matters is the difference between the counted `endAmount`
 * and the `calculatedEndAmount` the till worked out. Over or under, that is
 * what the page is opened to find.
 */

const get = <T>(url: string) => unwrap<T>(api.get<ApiEnvelope<T>>(url));

/** `CashDrawerMovementDto` — one cash event. */
export interface CashMovement {
  id: string;
  createdAt: string;
  comments: string | null;
  received: number | null;
  returned: number | null;
  orderId: string | null;
  checkInId: string | null;
  isDropIn: boolean;
  order: { id: string; confirmationCode: string | null } | null;
}

/** `CashDrawerCheckInDto` — one session on one lane. */
export interface CashCheckIn {
  id: string;
  startTime: string;
  endTime: string | null;
  startAmount: number;
  endAmount: number | null;
  calculatedEndAmount: number | null;
  calculatedEndAmountAfterfact: number;
  /** Spelt without the final `e` server-side. */
  calculatedEndAmountDifferenc: number;
  balance: number;
  net: number;
  dropInTotal: number;
  totalReceived: number;
  totalReturned: number;
  profit: number;
  posLaneId: string | null;
  userId: string | null;
  commentsCount: number;
  movement: CashMovement[] | null;
  posLane: { id: string; tag: string | null } | null;
  user: { id: string; completeName: string | null } | null;
  approver: { id: string; completeName: string | null } | null;
}

/** `CashDrawerMovementReport` — the page's whole answer. */
export interface CashReport {
  total: number;
  totalProfit: number;
  totalNet: number;
  dropInTotal: number;
  balance: number;
  checkIns: CashCheckIn[] | null;
}

/** `CashDrawerMovementReportBindingModel`. */
export interface CashReportQuery {
  startDate: string;
  endDate: string;
  businessId: string;
  posLaneId: string;
  employeeId: string;
  page: number;
  size: number;
  includeMovement: boolean;
  onlyClosed: boolean;
  onlyOpen: boolean;
  onlyUnder: boolean;
  onlyOver: boolean;
}

export function useCashReport(query: CashReportQuery | null) {
  return useQuery({
    queryKey: ['cash-drawer', query],
    queryFn: () =>
      unwrap<CashReport>(api.post('pos/cashdrawer/movement/report', query)),
    placeholderData: keepPreviousData,
    enabled: !!query,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['cash-drawer'] });
}

// --------------------------------------------------------------- check-ins

/** `UpdateCashDrawerCheckInBindingModel`. */
export interface UpdateCheckInInput {
  id: string;
  startTime: string;
  endTime: string | null;
  startAmount: number;
  endAmount: number | null;
}

export function useUpdateCheckIn() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (input: UpdateCheckInInput) =>
      unwrap<CashCheckIn>(api.put('pos/cashdrawer/checkin', input)),
    onSuccess: invalidate,
  });
}

/**
 * Have the server work the closing figure out again from the movements.
 *
 * `previewOnly` says what *would* change without writing it, which is the
 * safer thing to offer first: the result reports how many were evaluated and
 * how many would move.
 */
export interface RecalculationResult {
  evaluated: number;
  changed: number;
  previewOnly: boolean;
}

export function useRecalculateCheckIn() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      checkInIds,
      previewOnly,
    }: {
      checkInIds: string[];
      previewOnly: boolean;
    }) =>
      unwrap<RecalculationResult>(
        api.post('pos/cashdrawer/checkin/recalculate', {
          checkInIds,
          previewOnly,
        })
      ),
    onSuccess: (_result, variables) => {
      if (!variables.previewOnly) invalidate();
    },
  });
}

// --------------------------------------------------------------- movements

/** The movements of one check-in, fetched on demand. It is a POST with no body. */
export function useCheckInMovements(checkInId: string | null) {
  return useQuery({
    queryKey: ['cash-drawer', 'movements', checkInId],
    queryFn: () =>
      unwrap<CashMovement[]>(
        api.post(`pos/cashdrawer/movement/report/${checkInId}`, {})
      ),
    enabled: !!checkInId,
  });
}

function useInvalidateMovements(checkInId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: ['cash-drawer', 'movements', checkInId],
    });
    void queryClient.invalidateQueries({ queryKey: ['cash-drawer'] });
  };
}

/** `CashDrawerMovementBindingModel`. */
export interface CreateMovementInput {
  checkInId: string;
  received: number | null;
  returned: number | null;
  comments: string | null;
  isDropIn: boolean;
}

export function useCreateMovement(checkInId: string | null) {
  const invalidate = useInvalidateMovements(checkInId);

  return useMutation({
    mutationFn: (input: CreateMovementInput) =>
      unwrap<unknown>(api.post('pos/cashdrawer/movement', input)),
    onSuccess: invalidate,
  });
}

/** `UpdateCashDrawerMovementBindingModel` — no times, only the amounts. */
export interface UpdateMovementInput {
  id: string;
  received: number | null;
  returned: number | null;
  comments: string | null;
  isDropIn: boolean;
}

export function useUpdateMovement(checkInId: string | null) {
  const invalidate = useInvalidateMovements(checkInId);

  return useMutation({
    mutationFn: (input: UpdateMovementInput) =>
      unwrap<unknown>(api.put('pos/cashdrawer/movement', input)),
    onSuccess: invalidate,
  });
}

export function useDeleteMovement(checkInId: string | null) {
  const invalidate = useInvalidateMovements(checkInId);

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(api.delete(`pos/cashdrawer/movement/${id}`)),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------- comments

/** `CashDrawerMovementCommentDto`. */
export interface CheckInComment {
  id: string;
  userCompleteName: string | null;
  userAvatar: string | null;
  createdAt: string;
  comments: string | null;
}

export function useCheckInComments(checkInId: string | null) {
  return useQuery({
    queryKey: ['cash-drawer', 'comments', checkInId],
    queryFn: () =>
      get<{ results: CheckInComment[] }>(
        `pos/cashdrawer/checkin/comments/${checkInId}`
      ),
    select: (data) => data.results ?? [],
    enabled: !!checkInId,
  });
}

/** Answers the whole comment list back, so it seeds the cache. */
export function useAddCheckInComment(checkInId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string) =>
      unwrap<{ results: CheckInComment[] }>(
        api.post('pos/cashdrawer/checkin/comments', {
          checkInId,
          comment: comment.trim(),
        })
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['cash-drawer', 'comments', checkInId], data);
      void queryClient.invalidateQueries({ queryKey: ['cash-drawer'] });
    },
  });
}

/** What the till counted against what it should have held. */
export function difference(checkIn: CashCheckIn) {
  if (checkIn.endAmount == null || checkIn.calculatedEndAmount == null) {
    return null;
  }
  return checkIn.endAmount - checkIn.calculatedEndAmount;
}
